/**
 * Shared Bank / Vehicle Loan Calculator Summary table (Bank cell modes as baseline).
 */
import React from 'react';
import {
    Box,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {MoneyField} from '@/components/common';
import {formatNumber} from '@/utils/dataUtils';

/** Summary split MoneyField: width only. Height locked inside MoneyField compact split (~28px). */
export const SUMMARY_MONEY_FIELD_SX = {
    width: '100%',
    minWidth: 'max-content',
    boxSizing: 'border-box',
} as const;

export type LoanSummaryCellMode = 'dash' | 'split' | 'plainMoney' | 'plainNumber';

export interface LoanSummaryCell {
    mode: LoanSummaryCellMode;
    value?: number;
    suffixValue?: number;
    suffixEditable?: boolean;
    onSuffixChange?: (n: number | null) => void;
    onEditCancel?: () => void;
    onCalcHoverChange?: (hovering: boolean) => void;
    errorText?: string;
}

export interface LoanSummaryCardProps {
    yoa: string;
    lastYoa: string;
    hideLastYoa?: boolean;
    wrapRef?: React.Ref<HTMLDivElement>;
    onMouseLeave?: () => void;
    interestYoa: LoanSummaryCell;
    interestLastYoa: LoanSummaryCell;
    currentYoa: LoanSummaryCell;
    currentLastYoa: LoanSummaryCell;
    nonCurrentYoa: LoanSummaryCell;
    nonCurrentLastYoa: LoanSummaryCell;
    balanceYoa: LoanSummaryCell;
    balanceLastYoa: LoanSummaryCell;
}

function renderCell(cell: LoanSummaryCell): React.ReactNode {
    if (cell.mode === 'dash') {
        return <Typography variant="body2">-</Typography>;
    }
    if (cell.mode === 'plainNumber') {
        const n = Number.isFinite(cell.value) ? (cell.value as number) : 0;
        return formatNumber(n);
    }
    if (cell.mode === 'plainMoney') {
        return (
            <MoneyField
                align="center"
                compact
                value={cell.value}
                onCalcHoverChange={cell.onCalcHoverChange}
                sx={SUMMARY_MONEY_FIELD_SX}
            />
        );
    }
    const editable = Boolean(cell.suffixEditable);
    return (
        <Box>
            <MoneyField
                align="center"
                compact
                value={cell.value}
                separator=">"
                suffixValue={cell.suffixValue}
                suffixReadOnly={!editable}
                icon={editable ? <EditIcon fontSize="small"/> : undefined}
                cancelIcon={editable ? <CloseIcon fontSize="small"/> : undefined}
                onSuffixChange={editable ? cell.onSuffixChange : undefined}
                onEditCancel={editable ? cell.onEditCancel : undefined}
                onCalcHoverChange={cell.onCalcHoverChange}
                sx={SUMMARY_MONEY_FIELD_SX}
            />
            {!!cell.errorText && (
                <Typography variant="caption" color="error">{cell.errorText}</Typography>
            )}
        </Box>
    );
}

export default function LoanSummaryCard({
    yoa,
    lastYoa,
    hideLastYoa = false,
    wrapRef,
    onMouseLeave,
    interestYoa,
    interestLastYoa,
    currentYoa,
    currentLastYoa,
    nonCurrentYoa,
    nonCurrentLastYoa,
    balanceYoa,
    balanceLastYoa,
}: LoanSummaryCardProps) {
    const rows: Array<{label: string; yoaCell: LoanSummaryCell; lastCell: LoanSummaryCell}> = [
        {label: 'Interest', yoaCell: interestYoa, lastCell: interestLastYoa},
        {label: 'Current', yoaCell: currentYoa, lastCell: currentLastYoa},
        {label: 'Non-Current', yoaCell: nonCurrentYoa, lastCell: nonCurrentLastYoa},
        {label: 'Balance', yoaCell: balanceYoa, lastCell: balanceLastYoa},
    ];
    return (
        <Box ref={wrapRef} sx={{px: 2, pb: 2}} onMouseLeave={onMouseLeave}>
            <Card>
                <TableContainer>
                    <Table size="small" sx={{tableLayout: 'auto', width: 'max-content', minWidth: '100%'}}>
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={hideLastYoa ? 2 : 3} align="center">
                                    <Typography variant="subtitle2" sx={{mb: 1}}>Summary</Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="center" sx={{whiteSpace: 'nowrap'}}/>
                                <TableCell align="center" sx={{whiteSpace: 'nowrap', minWidth: 120}}>
                                    <Typography variant="body1" sx={{fontWeight: 500}}>{yoa}</Typography>
                                </TableCell>
                                {!hideLastYoa && (
                                    <TableCell align="center" sx={{whiteSpace: 'nowrap', minWidth: 120}}>
                                        <Typography variant="body1" sx={{fontWeight: 500}}>{lastYoa}</Typography>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.label}>
                                    <TableCell sx={{whiteSpace: 'nowrap', verticalAlign: 'middle'}}>
                                        {row.label}
                                    </TableCell>
                                    <TableCell align="center" sx={{whiteSpace: 'nowrap', verticalAlign: 'middle'}}>
                                        {renderCell(row.yoaCell)}
                                    </TableCell>
                                    {!hideLastYoa && (
                                        <TableCell align="center" sx={{whiteSpace: 'nowrap', verticalAlign: 'middle'}}>
                                            {renderCell(row.lastCell)}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Box>
    );
}
